<template>
  <section v-if="timeSheet && timeSheet.timeSheetEntries.length" class="details-header">
    <div>
      <object-header :header-data="objectHeader" />
    </div>
    <div class="header-title">
      <div class="logo">
        <img src="images/logo-initials" />
      </div>

      <div class="sub-header">
        {{ $t('timeSheets.details.timeSheet') }}
        #{{ timeSheet.referenceNumber }}
      </div>
      <div>
        <Status :status="timeSheet.status" object-type="time-sheet" />
      </div>
      <div class="time-sheet-minor-actions">
        <VueHtml2pdf
          ref="DownloadComp"
          :show-layout="false"
          :paginate-elements-by-height="1400"
          pdf-format="a4"
          :manual-pagination="false"
          :enable-download="false"
          :preview-modal="false"
          :filename="`Timesheet-${timeSheet.referenceNumber}`"
          @beforeDownload="beforeDownload($event)"
        >
          <section slot="pdf-content">
            <TimeSheetDetailsPdf :time-sheet="timeSheet" />
          </section>
        </VueHtml2pdf>
        <TolButton
          :tooltip="$t('timeSheet.actions.saveAsPdf')"
          button-type="minor"
          icon-name="file-pdf"
          class="action-round"
          @click="exportAsPdf"
        />
        <template v-if="canSave">
          <TolButton
            :tooltip="$t('timeSheet.actions.save')"
            button-type="minor"
            icon-name="pencil-alt"
            class="action-round"
            @click="$emit('save-time-sheet')"
          />
        </template>
        <template v-if="canReopen">
          <TolButton
            :tooltip="$t('timeSheet.actions.reopen')"
            button-type="minor"
            icon-name="lock-open"
            class="action-round"
            @click="$emit('reopen-time-sheet')"
          />
        </template>
      </div>
    </div>
    <CreatedComment :comment="timeSheet.approverComment" />
    <div class="header-rows">
      <div class="header-info">
        <div class="header-label">{{ $t('timeSheet.labels.contractor') }}</div>
        <div class="header-value">
          <router-link
            class="contacts-list-link"
            :class="{ disabled: !isTesEmployee }"
            :to="`/contractors/${timeSheet.assignment.contractor.reference_number}`"
          >
            <ContactsList :contacts="[timeSheet.contractor]" />
          </router-link>
        </div>
      </div>
      <div class="header-info">
        <div class="header-label">{{ $t('timeSheet.labels.payrollNumber') }}</div>
        <div class="header-value">
          {{ payrollNumber }}
        </div>
      </div>
      <div class="header-info">
        <div class="header-label">{{ $t('timeSheet.labels.client') }}</div>
        <router-link
          class="clients-display-link"
          :to="`/clients/${timeSheet.assignment.client_reference}`"
        >
          <ClientsDisplay :contacts="[{ lastName: timeSheet.assignment.client_name }]" />
        </router-link>
      </div>
      <div class="header-info">
        <div class="header-label">{{ $t('timeSheet.labels.endClient') }}</div>
        <div v-if="timeSheet.assignment.end_client_name" class="header-value">
          {{ timeSheet.assignment.end_client_name }}
        </div>
        <div v-else class="header-value">
          {{ $t('timeSheet.labels.none') }}
        </div>
      </div>
      <div class="header-info">
        <div class="header-label">{{ $t('timeSheet.labels.assignment') }}</div>
        <router-link
          class="clients-display-link"
          :to="`/assignments/${timeSheet.assignment.reference_number}`"
        >
          <AssignmentDisplayLink :assignment="timeSheet.assignment" />
        </router-link>
      </div>
      <div class="header-info approver">
        <div class="header-label">{{ $t('timeSheet.labels.approvers') }}</div>
        <div class="header-value approver-name">
          <ContactsList :contacts="timeSheet.approvers" contact-type="approver" />
        </div>
      </div>
      <div class="header-info">
        <div class="header-label">{{ $t('timeSheet.labels.period') }}</div>
        <div class="header-value">
          <span class="nowrap">{{ startDate | formatDate }}</span>
          to
          <span class="nowrap">{{ endDate | formatDate }}</span>
        </div>
      </div>
      <div class="header-info">
        <div class="header-label">{{ $t('timeSheet.labels.notes') }}</div>
        <div class="header-value">
          {{ timeSheet.ibm_end_client }}
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import AssignmentDisplayLink from '@/app/components/AssignmentDisplayLink';
import ContactsList from '@/app/components/ContactsList';
import ClientsDisplay from '@/app/components/ClientsDisplay';
import Status from '@/app/components/Status';
import TolButton from '@/app/components/TolButton';
import CreatedComment from '@/app/components/CreatedComment';
import { userUtils } from '@/mixins/user-utils';
import TimeSheetDetailsPdf from './TimeSheetDetailsPdf.vue';
import format from 'date-fns/format';
import ObjectHeader from '@/app/components/ObjectHeader';
import formatDateTime from '@/filters/format-datetime.filter.js';

export default {
  name: 'TimeSheetBodyHeader',
  components: {
    Status,
    TolButton,
    ClientsDisplay,
    ContactsList,
    CreatedComment,
    AssignmentDisplayLink,
    TimeSheetDetailsPdf,
    ObjectHeader
  },
  mixins: [userUtils],
  props: {
    timeSheet: {
      type: Object,
      required: true
    },
    canSave: {
      type: Boolean,
      required: true
    },
    canReopen: {
      type: Boolean,
      required: true
    }
  },
  computed: {
    // startDate and endDate computed functions are only required right now because
    // the api does not return these values. Once the api has been changed to return
    // all the fields from the time_sheets_view, we won't need these two functions.
    startDate() {
      return this.timeSheet.timeSheetEntries[0].entryDate;
    },
    endDate() {
      return this.timeSheet.timeSheetEntries[this.timeSheet.timeSheetEntries.length - 1].entryDate;
    },
    payrollNumber() {
      return this.timeSheet.assignment.payroll_number;
    },
    objectHeader() {
      const data = {
        referenceNumber: this.timeSheet.referenceNumber,
        objectType: 'TIME_SHEET',
        lastModified: null
      };
      const lastModified = `${this.timeSheet.createdBy.firstName} ${
        this.timeSheet.createdBy.lastName
      } at ${formatDateTime(this.timeSheet.createdAt)}`;

      //if (this.isTesEmployee) {
      data.lastModified = lastModified;
      //}

      return data;
    }
  },
  methods: {
    exportAsPdf() {
      this.$refs.DownloadComp.generatePdf();
    },
    async beforeDownload({ html2pdf, options, pdfContent }) {
      await html2pdf()
        .set(options)
        .from(pdfContent)
        .toPdf()
        .get('pdf')
        .then(pdf => {
          const totalPages = pdf.internal.getNumberOfPages();
          for (let i = 1; i <= totalPages; i++) {
            pdf.setPage(i);
            pdf.setFontSize(10);
            pdf.setTextColor(150);
            pdf.text(
              'Generated by TimeOnline at ' + format(new Date(), 'YYYY-MM-DD HH:M'),
              pdf.internal.pageSize.getWidth() * 0.05,
              pdf.internal.pageSize.getHeight() - 0.3
            );
            pdf.text(
              'Page ' + i + ' of ' + totalPages,
              pdf.internal.pageSize.getWidth() * 0.88,
              pdf.internal.pageSize.getHeight() - 0.3
            );
          }
        })
        .save();
    }
  }
};
</script>

<style lang="scss" scoped>
@import '../../../../../styles/variables.scss';
@import '../../../../../styles/colors.scss';

.details-header {
  background-color: $background-level-2;
  .header-title {
    padding: $details-padding;
    display: flex;
    align-items: center;
    .time-sheet-minor-actions {
      flex-grow: 1;
      text-align: right;
    }
    .button-separator {
      margin: 0 ($details-padding/2);
      display: inline-block;
    }

    .logo {
      display: none;
      text-align: middle;
      img {
        width: 75px;
        height: 30px;
        border: none;
      }
    }
  }
  .header-rows {
    padding: $time-sheet-header;
    display: grid;
    grid-template-columns: 1fr 1fr 1fr 1fr;
  }
  .header-info {
    padding: 10px 5px;
    font-weight: bold;
    &.assignment {
      font-weight: normal;
    }
  }

  .contacts-list-link {
    text-decoration: none;
    color: $black;
    box-shadow: inset 0 -3px 0 0 #bdb;
    &.disabled {
      pointer-events: none;
    }
  }

  .clients-display-link {
    text-decoration: none;
    color: $black;
    box-shadow: inset 0 -3px 0 0 #bdb;
  }
  .header-label {
    text-transform: uppercase;
    font-size: 15px;
    font-weight: bold;
    margin-bottom: 20px;
    color: $mid-grey-2;
  }
}

@media print {
  .details-header {
    background-color: $white;
    position: relative;

    .header-title {
      padding: $pdf-header-padding;
      text-align: center;
      .sub-header {
        width: 100%;
        font-size: 22px;
        line-height: 24px;
        margin-top: 8px;
        margin-bottom: -8px;
      }
      .time-sheet-minor-actions {
        .button-separator {
          display: none;
        }
      }
      .logo {
        display: block;
        position: absolute;
        left: 0px;
        top: 3px;
      }
      .status {
        position: absolute;
        right: 0px;
        top: 3px;
      }
    }

    .header-rows {
      padding: 5px 0 0 0;
      margin-bottom: 5px;
      .header-info {
        width: 33%;
        padding: 0px;
        .header-label,
        .header-value {
          font-size: 11pt;
        }

        &.assignment,
        &.approver {
          display: none;
        }
      }
    }

    > hr {
      margin-top: 15px;
      margin-bottom: 0px;
    }
  }
}

@media only screen and (max-width: 800px) {
  .details-header {
    font-size: 0.8rem;
    .header-rows {
      display: flex;
      flex-direction: column;
      padding: $responsive-header-padding-sm;
      .header-info {
        width: 100%;
      }
    }

    .header-label {
      font-size: 0.8rem;
      margin-bottom: 5px;
    }

    .header-value {
      font-size: 0.8rem;
    }
  }
}
</style>
